import React from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Menu, Radio, Radar as RadarIcon, Bell, Tv, Video, LockKeyhole, SlidersHorizontal, Youtube, Instagram, Facebook, CloudSun, Info, Mail, ChevronDown } from "lucide-react";
import { SITE, SOCIALS } from "../config";
import AlertsTicker from "./AlertsTicker";
import BottomChyron from "./BottomChyron";
import ErrorBoundary from "./ErrorBoundary";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

// Primary nav (visible on desktop)
const primaryNav = [
  { to: "/", label: "Home", icon: Radio },
  { to: "/radar", label: "Radar", icon: RadarIcon },
  { to: "/forecast", label: "Forecast", icon: CloudSun },
  { to: "/watch", label: "Live Broadcast", icon: Tv },
  { to: "/skycams", label: "Skycams", icon: Video },
  { to: "/alerts", label: "Alerts", icon: Bell },
];

// Secondary nav (inside "More" dropdown)
const moreNav = [
  { to: "/about", label: "About", icon: Info },
  { to: "/contact", label: "Contact", icon: Mail },
  { to: "/ops", label: "Studio", icon: SlidersHorizontal },
];

// All items for footer + mobile drawer
const allNav = [...primaryNav, ...moreNav];

const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 1.6 1.1 3 2.4 3.9 1 .7 2.2 1.1 3.4 1.1v3.3c-1.7 0-3.4-.5-4.9-1.4v6.5a6.4 6.4 0 1 1-6.4-6.4c.4 0 .8 0 1.2.1v3.4a3 3 0 1 0 2 2.8V3h2.3z"/>
  </svg>
);

const socialIcon = (key, cls = "w-4 h-4") => {
  switch (key) {
    case "youtube": return <Youtube className={cls} />;
    case "tiktok": return <TikTokIcon className={cls} />;
    case "instagram": return <Instagram className={cls} />;
    case "facebook": return <Facebook className={cls} />;
    default: return null;
  }
};

const BANNER_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_desk-custom/artifacts/dleu4wtz_IMG_6894.webp";

const Brand = () => (
  <Link to="/" className="flex items-center group h-full">
    <img src={BANNER_IMG} alt="Storm Desk Live" className="h-10 sm:h-12 w-auto object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]" />
  </Link>
);

const NavLinks = ({ onNavigate, all = false }) => {
  const list = all ? allNav : primaryNav;
  return (
    <nav className="flex md:items-center gap-1 flex-col md:flex-row">
      {list.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          onClick={onNavigate}
          className={({ isActive }) =>
            `px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
              isActive
                ? "text-white bg-white/[0.06] shadow-inner"
                : "text-slate-300 hover:text-white hover:bg-white/[0.04]"
            }`
          }
        >
          <Icon className="w-4 h-4 opacity-80" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
};

const MoreMenu = () => {
  const loc = useLocation();
  const activeInMore = moreNav.some(n => loc.pathname === n.to);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${
            activeInMore
              ? "text-white bg-white/[0.06]"
              : "text-slate-300 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          More <ChevronDown className="w-4 h-4 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0a0e18] border-white/10 text-slate-200 min-w-[180px]">
        {moreNav.map(({ to, label, icon: Icon }) => (
          <DropdownMenuItem key={to} asChild className="focus:bg-white/10 focus:text-white cursor-pointer">
            <Link to={to} data-testid={`more-nav-${label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-slate-400" />
              <span>{label}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Socials = ({ compact = false }) => (
  <div className={`flex items-center ${compact ? "gap-1" : "gap-1.5"}`}>
    {SOCIALS.map((s) => (
      <a
        key={s.key}
        href={s.href}
        target="_blank"
        rel="noreferrer noopener"
        title={s.name}
        aria-label={s.name}
        className="w-8 h-8 rounded-md grid place-items-center text-slate-300 hover:text-white bg-white/[0.03] hover:bg-white/[0.09] border border-white/5 transition-colors"
      >
        {socialIcon(s.key)}
      </a>
    ))}
  </div>
);

const Header = () => {
  const loc = useLocation();
  return (
    <header className="sticky top-0 z-40">
      {/* Storm Desk branded banner strip - full width, above everything */}
      <div className="relative h-24 sm:h-32 bg-black overflow-hidden">
        <img
          src={BANNER_IMG}
          alt="Storm Desk Live"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      </div>
      <AlertsTicker />
      <div className="backdrop-blur-md bg-[rgba(6,8,14,0.85)] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="hidden lg:flex items-center gap-1">
              <NavLinks />
              <MoreMenu />
            </div>
            <div className="lg:hidden">
              <Link to="/" className="text-sm font-bold display text-white">Storm Desk Live</Link>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex"><Socials /></div>
            <Link to="/ops" data-testid="header-studio-link">
              <Button variant="outline" className="hidden sm:inline-flex h-10 gap-2 border-white/15 bg-white/[0.03] text-slate-100 hover:bg-white/[0.08] hover:text-white">
                <SlidersHorizontal className="w-4 h-4 text-cyan-400" />
                Studio
              </Button>
            </Link>
            <Link to="/watch">
              <Button className="bg-red-600 hover:bg-red-500 text-white gap-2 border-0 shadow-[0_0_24px_-8px_rgba(239,68,68,0.9)]">
                <span className="w-2 h-2 rounded-full bg-white live-dot" />
                Watch Live
              </Button>
            </Link>
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button size="icon" variant="ghost" className="text-slate-200">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-[#0a0e18] border-white/5 text-slate-200 w-[85vw] max-w-sm">
                  <div className="mt-6 space-y-6">
                    <div className="h-12 relative overflow-hidden rounded-md">
                      <img src={BANNER_IMG} alt="Storm Desk" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <NavLinks all />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Socials</div>
                      <Socials />
                    </div>
                    <div className="text-[11px] mono text-slate-500">Route: {loc.pathname}</div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer className="mt-20 border-t border-white/5 bg-[rgba(5,7,13,0.6)] pb-24 lg:pb-20">
    <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-8">
      <div>
        <div className="display font-bold text-lg">{SITE.name}</div>
        <p className="text-sm text-slate-400 mt-2 max-w-sm">{SITE.description}</p>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Explore</div>
        <ul className="space-y-2 text-sm text-slate-300">
          {allNav.map(n=>(
            <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Follow the chase</div>
        <div className="flex gap-2"><Socials /></div>
        <div className="text-[11px] mono text-slate-500 mt-6">© {new Date().getFullYear()} {SITE.name}. Data via NWS, RainViewer, and public feeds.</div>
        <Link
          to="/ops"
          data-testid="footer-operator-login-link"
          className="mt-4 inline-flex items-center gap-1.5 text-[10px] mono uppercase tracking-widest text-slate-600 hover:text-slate-300 transition-colors"
        >
          <LockKeyhole className="w-3 h-3" /> Operator login
        </Link>
      </div>
    </div>
  </footer>
);

const Layout = () => (
  <>
    <Header />
    <main className="min-h-[70vh] pb-24"><ErrorBoundary><Outlet /></ErrorBoundary></main>
    <Footer />
    <BottomChyron />
  </>
);

export default Layout;
